import { higginsColors } from '@higgins/ui';
import type { MatrixData, ChartFormat } from '@/lib/types';

// Higgins theme: green=#93b856 (idx 5), yellow=#e3a711 (idx 0), red=#c56055 (idx 1)
const [THEME_YELLOW, THEME_RED, , , , THEME_GREEN] = higginsColors;

interface MatrixTableProps {
  data: MatrixData;
  unit?: ChartFormat;
}

function fmtValue(v: number | string | null, unit?: ChartFormat): string {
  if (v == null) return '–';
  if (typeof v === 'string') return v;
  if (unit === 'currency') {
    const s = v < 0 ? '-$' : '$';
    return `${s}${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  if (unit === 'percent') return `${(v * 100).toFixed(1)}%`;
  return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function cellStyle(pct: number | null): React.CSSProperties | undefined {
  if (pct == null) return undefined;
  // PBI returns % Budget measures as decimals (0.95 = 95%, 1.05 = 105%).
  if (pct < 0.95) return { backgroundColor: THEME_RED,    color: '#fff' };
  if (pct < 1.00) return { backgroundColor: THEME_YELLOW, color: '#000' };
  return             { backgroundColor: THEME_GREEN,  color: '#fff' };
}

export function MatrixTable({ data, unit }: MatrixTableProps) {
  const { columns, rows, colFormats } = data;

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        No data
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th
              className="sticky left-0 z-10 bg-slate-700 text-left font-medium text-white py-1.5 px-2 border-r border-slate-600"
              style={{ minWidth: 130 }}
            />
            {columns.map(col => (
              <th
                key={col}
                className="bg-slate-700 text-center font-medium text-white py-1.5 px-2 whitespace-nowrap"
                style={{ minWidth: 90 }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(() => {
            let dataRowIdx = 0;
            return rows.map((row, ri) => {
              if (row.label === '') {
                return (
                  <tr key={ri}>
                    <td colSpan={columns.length + 1} className="p-0 h-3 bg-slate-300 border-0" />
                  </tr>
                );
              }
              const stripe = dataRowIdx++ % 2 === 0 ? 'bg-white' : 'bg-muted/20';
              return (
                <tr key={ri} className={stripe}>
                  <td className="sticky left-0 z-10 bg-inherit py-1.5 px-2 font-medium border-r border-border/40 whitespace-nowrap">
                    {row.label}
                  </td>
                  {row.values.map((val, ci) => {
                    const pct = colFormats?.[ci]?.[ri] ?? null;
                    const style = cellStyle(pct);
                    return (
                      <td
                        key={ci}
                        className="text-center py-1.5 px-2 tabular-nums"
                        style={style}
                      >
                        {fmtValue(val, unit)}
                      </td>
                    );
                  })}
                </tr>
              );
            });
          })()}
        </tbody>
      </table>
    </div>
  );
}
